import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import React, { type PropsWithChildren, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { MbButton } from '../../atoms';

export type MbDialogProps = PropsWithChildren<{
  open?: boolean;
  title?: string;
  content?: string | string[];
  okLabel?: string;
  cancelLabel?: string;
  onOk?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
}>;

export type MbDialogRef = {
  show: () => Promise<boolean>;
};
const MbDialogInner = (props: MbDialogProps, ref: React.ForwardedRef<MbDialogRef>) => {
  const { open, title, content, okLabel, cancelLabel, onOk, onCancel, onClose, children } = props;
  const [dialogOpen, setDialogOpen] = useState(open || false);
  useEffect(() => {
    setDialogOpen(open || false);
  }, [open]);

  const okRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const resolveRef = useRef<(value: boolean) => void>();

  const handleClose = () => {
    setDialogOpen(false);
    onClose?.();
    resolveRef.current?.(false);
  };

  const show = useCallback(() => {
    setDialogOpen(true);

    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  useImperativeHandle(ref, () => ({
    show,
  }));

  const contentStyle = okLabel || cancelLabel ? {} : { paddingBottom: 0 };

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
          width: '90%',
          maxWidth: '400px',
          margin: '16px',
        },
      }}
    >
      {title && <DialogTitle sx={{ color: 'black', fontWeight: '600', textAlign: 'center' }}>{title}</DialogTitle>}
      <DialogContent sx={{ ...contentStyle, paddingY: 4 }}>
        {content &&
          Array.isArray(content) &&
          content.map((item) => (
            <DialogContentText
              key={item}
              sx={{ color: '#1f2937', fontWeight: '600', textAlign: 'center', fontSize: '1.25rem' }}
            >
              {item}
            </DialogContentText>
          ))}
        {content && !Array.isArray(content) && (
          <DialogContentText sx={{ color: '#1f2937', fontWeight: '600', textAlign: 'center', fontSize: '1.25rem' }}>
            {content}
          </DialogContentText>
        )}
        {children}
      </DialogContent>
      {(okLabel || cancelLabel) && (
        <DialogActions className="action-button-group">
          {cancelLabel && (
            <MbButton
              ref={cancelRef}
              btnType="secondary"
              variant="outlined"
              className="action-button action-button-secondary"
              onClick={() => {
                onCancel?.();
                handleClose();
              }}
              size="large"
              fullWidth
            >
              {cancelLabel}
            </MbButton>
          )}
          {okLabel && (
            <MbButton
              ref={okRef}
              btnType="secondary"
              variant="contained"
              className="action-button action-button-primary"
              onClick={() => {
                onOk?.();
                setDialogOpen(false);
                resolveRef.current?.(true);
              }}
              size="large"
              fullWidth
            >
              {okLabel}
            </MbButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export const MbDialog = React.forwardRef(MbDialogInner);
